<template>
  <section class="simulator">
    <div class="form-group group-container-graph">
      <canvas v-if="eventsSelected.length && period.length" id="challenge-chart"></canvas>
      <div v-else class="empty-list">Any period or any event selected</div>
    </div>
    <div class="form-group-container form-group-container-line-1">
      <div class="form-group form-group-container-period">
        <label style="display:block">{{ $t('common.period') }}</label>
        <el-date-picker
          v-model="period"
          type="daterange"
          :start-placeholder="$t('campaign.modal.create.fields.date.start')"
          :end-placeholder="$t('campaign.modal.create.fields.date.end')"
          @change="onPeriodSelected"
        >
        </el-date-picker>
      </div>
      <div class="form-group form-group-container-events">
        <label style="display:block">{{ $t('common.events') }}</label>
        <div style="display:grid; grid-template-columns:4fr 1fr">
          <el-select v-model="event" placeholder="Select">
            <el-option v-for="event in eventsSorted" :key="event.id" :label="event.alias" :value="event.id"></el-option>
          </el-select>
          <el-button @click="addEvent">
            <font-awesome-icon icon="plus"></font-awesome-icon>
          </el-button>
        </div>
      </div>
      <div class="form-group">
        <label style="display:block">Energy</label>
        <el-input type="text" v-model="energy" v-on:keyup.native="simulate" :disabled="!chart">
          <el-button slot="append" @click="setDefaultEnergy">
            <font-awesome-icon icon="eraser"></font-awesome-icon>
          </el-button>
        </el-input>
      </div>
    </div>
    <div class="form-group-container">
      <div class="form-group form-group-container-events-selected">
        <label style="display:block;margin-bottom:8px;">Events selected</label>
        <div v-if="eventsSelected.length" class="listing-events-challenge">
          <div class="element" v-for="(eventId, index) in eventsSelected" :key="eventId">
            <div>
              <span class="name" :style="{ color: colors[index], fontWeight: '600' }">{{
                getEventById(eventId).alias
              }}</span>
              <el-button type="text" class="is-danger" @click="removeEvent(eventId, index)">
                <font-awesome-icon icon="trash"></font-awesome-icon>
              </el-button>
            </div>
            <el-slider
              class="slider"
              v-model="configEvents[eventId]"
              :max="5"
              @change="simulate"
              show-stops
              :disabled="!chart"
              :step="1"
            >
            </el-slider>
          </div>
        </div>
        <div v-else class="listing-events-challenge empty-list">
          Any event selected
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment-timezone'
import { Chart } from 'chart.js'
import HTTP from '@rocketfid/api'

export default {
  props: ['id'],
  data() {
    return {
      type: 'monster',
      activationdate: null,
      deactivationdate: null,
      period: [],
      event: null,
      eventsSelected: [],
      colors: ['#00b894', '#00cec9', '#fdcb6e', '#6c5ce7', '#0984e3'],
      chart: null,
      energy: 0,
      energyDefault: 0,
      configEvents: {}
    }
  },
  computed: {
    ...mapGetters('account', {
      site: 'currentSite',
      lang: 'defaultLang'
    }),
    ...mapGetters('events', {
      events: 'events',
      getEventById: 'getById'
    }),
    ...mapGetters('challenges', {
      getById: 'getById'
    }),
    eventsSorted() {
      return this.events.filter(event => !this.eventsSelected.includes(event.id))
    }
  },
  watch: {
    activationdate() {
      this.simulate()
    },
    deactivationdate() {
      this.simulate()
    },
    eventsSelected() {
      this.simulate()
    }
  },
  methods: {
    ...mapActions('events', ['loadEvents']),

    setDefaultEnergy() {
      this.energy = this.energyDefault
      this.simulate()
    },
    onPeriodSelected(period) {
      if (period) {
        if (period[0]) {
          const today = moment().tz(this.site.timezone)
          let diffDays =
            today.diff(
              moment(period[1])
                .endOf('day')
                .add(
                  moment(period[1])
                    .tz(this.site.timezone)
                    .utcOffset() * -1,
                  'minutes'
                ),
              'days'
            ) * -1

          if (diffDays > 0) {
            let zoneActivation =
              moment(period[0])
                .tz(this.site.timezone)
                .utcOffset() * -1
            this.activationdate =
              moment(period[0])
                .startOf('day')
                .add(zoneActivation, 'minutes')
                .subtract(diffDays, 'days')
                .format('YYYY-MM-DD[T]HH:mm:ss') + 'Z'
            this.deactivationdate =
              moment(period[1])
                .endOf('day')
                .add(zoneActivation, 'minutes')
                .subtract(diffDays, 'days')
                .format('YYYY-MM-DD[T]HH:mm:ss') + 'Z'
          } else {
            this.activationdate = this._setDate(this.period[0], this.site.timezone, true)
            this.deactivationdate = this._setDate(this.period[1], this.site.timezone, false)
          }
        }
      } else {
        this.activationdate = null
        this.deactivationdate = null
        this.$emit('reset')
      }
    },
    addEvent() {
      this.configEvents[this.event] = 2
      this.energy = null
      this.energyDefault = null
      if (this.event != null) {
        this.eventsSelected.push(this.event)
      }
      this.event = null
    },
    removeEvent(id, index) {
      this.energy = null
      this.energyDefault = null
      delete this.configEvents[id]
      this.eventsSelected.splice(index, 1)

      if (this.eventsSelected.length < 1) {
        this.$emit('reset')
      }
    },

    simulate() {
      if (!this.activationdate || !this.deactivationdate || this.eventsSelected.length <= 0) {
        if (this.chart) {
          this.chart.destroy()
          this.chart = null
        }
        return
      }
      let events = {}

      // Construct legends
      let labels = []
      const dateStart = this._setDate(this.period[0], this.site.timezone, true)
      let dateCurrent = moment(dateStart)
      let dateEnd = this._setDate(this.period[1], this.site.timezone, false)
      while (dateCurrent.diff(dateEnd) <= 0) {
        labels.push(dateCurrent.format('YYYY-MM-DD'))
        dateCurrent.add(1, 'days')
      }

      dateEnd = moment(this.deactivationdate)

      // Construct array of each event
      this.eventsSelected.forEach(id => {
        events[this.getEventById(id).alias] = {}
      })

      // Popuplate events array with all dates with 0 donecount default
      dateCurrent = moment(this.activationdate)
      while (dateCurrent.diff(dateEnd) <= 0) {
        this.eventsSelected.forEach(id => {
          events[this.getEventById(id).alias][dateCurrent.format('YYYY-MM-DD')] = 0
        })
        dateCurrent.add(1, 'days')
      }

      // Get donecount of each events
      let promises = []
      this.eventsSelected.forEach(id => {
        let alias = this.getEventById(id).alias
        let promise = HTTP.activity
          .getDetails({ activationdate: this.activationdate, deactivationdate: this.deactivationdate, alias: alias })
          .then(result => {
            result.forEach(data => {
              events[alias][data.day] = data.done * this.configEvents[id]
            })
          })

        promises.push(promise)
      })

      // Clean for dataset
      Promise.all(promises).then(() => {
        let datasets = []
        let index = 0

        for (let alias in events) {
          if (events.hasOwnProperty(alias)) {
            let event = events[alias]
            let dataset = {
              label: alias,
              data: [],
              backgroundColor: this.colors[index],
              borderColor: this.colors[index],
              borderWidth: 1,
              pointBorderWidth: 1,
              pointRadius: 1,
              pointHitRadius: 1,
              yAxisID: 'EVT'
            }
            for (let date in event) {
              if (event.hasOwnProperty(date)) {
                dataset.data.push(event[date])
              }
            }
            datasets.push(dataset)
          }
          index++
        }

        let monster = this.simulateMonster(datasets)
        datasets.unshift(monster)

        let dataRetrieved = {
          type: 'line',
          data: {
            labels: labels,
            datasets: datasets
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            tooltips: { enabled: false },
            legend: { display: false },
            spanGaps: true,
            showLines: true,
            scales: {
              xAxis: {
                gridLines: { display: false },
                display: true,
                ticks: { maxRotation: 80 }
              },
              EVT: {
                position: 'right',
                display: false,
                stacked: true,
                ticks: {
                  beginAtZero: true
                },
                gridLines: { display: false }
              },
              MST: {
                position: 'left',
                ticks: {
                  beginAtZero: true,
                  min: 0
                },
                gridLines: { display: false }
              }
            }
          }
        }
        this.createChart('challenge-chart', dataRetrieved)

        // Send data to the parent
        let activationdate = this._setDate(this.period[0], this.site.timezone, true)
        let deactivationdate = this._setDate(this.period[1], this.site.timezone, false)
        this.$emit('config', {
          energy: this.energy,
          events: this.configEvents,
          activationdate: activationdate,
          deactivationdate: deactivationdate
        })
      })
    },

    createChart(chartId, chartData) {
      const ctx = document.getElementById(chartId)
      if (this.chart) {
        this.chart.destroy()
        this.chart = null
      }
      this.chart = new Chart(ctx, {
        type: chartData.type,
        data: chartData.data,
        options: chartData.options
      })
    },

    simulateMonster(datasets) {
      let monster = []
      let doneCountTotal = 0

      if (this.type === 'monster') {
        // Get total donecount
        datasets.forEach(dataset => {
          dataset.data.forEach(donecount => {
            doneCountTotal = doneCountTotal + donecount
          })
        })

        this.energyDefault = doneCountTotal

        if (this.energy) {
          doneCountTotal = this.energy
        } else {
          this.energy = doneCountTotal
        }

        // Set default
        let index = 0
        let dateCurrent = moment(this.activationdate)
        let dateEnd = moment(this.deactivationdate)
        while (dateCurrent.diff(dateEnd) <= 0) {
          let done = 0
          datasets.forEach(dataset => {
            for (let i = 0; i <= index; i++) {
              done += dataset.data[i]
            }
          })
          monster[index] = doneCountTotal - done
          index++
          dateCurrent.add(1, 'days')
        }
      }

      return {
        label: 'Monster',
        data: monster,
        fill: false,
        borderColor: '#F00',
        borderWidth: 1,
        pointBorderWidth: 1,
        pointRadius: 1,
        pointHitRadius: 1,
        yAxisID: 'MST'
      }
    }
  },
  mounted() {
    this.loadEvents().then(() => {
      if (!this.id) {
        return
      }
      let challenge = JSON.parse(JSON.stringify(this.getById(this.id)))
      this.energy = challenge.energy
      this.period = []
      this.period.push(this._getDate(challenge.activationdate, this.site.timezone))
      this.period.push(this._getDate(challenge.deactivationdate, this.site.timezone))
      challenge.events.forEach(event => {
        this.eventsSelected.push(event.id)
        this.configEvents[event.id] = event.damage ? event.damage : event.score
      })

      this.onPeriodSelected(this.period)
    })
  }
}
</script>

<style lang="scss" scoped>
.group-container-graph {
  width: 100%;
  height: 280px;
  border-radius: 8px;
}

.simulator {
  width: 100%;
  height: 100%;
  display: block;
}
.element {
  margin-bottom: 12px;
  display: inline-block;
}

.form-group-container-line-1 {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 18px;
}

.listing-events-challenge {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 12px;
  height: 140px;
  overflow-y: auto;

  &.empty-list {
    text-align: center;
    width: 100%;
    display: block;
    line-height: 130px;
  }

  > .element {
    background: #fafafa;
    border: 1px dashed #e4e7ed;
    border-radius: 4px;
    padding: 12px;
  }
}

.group-container-graph .empty-list {
  background: #fafafa;
  border: 1px dashed #e4e7ed;
  border-radius: 4px;
  text-align: center;
  width: 100%;
  display: block;
  line-height: 280px;
  height: 100%;
}
</style>



// WEBPACK FOOTER //
// src/components/challenge/challenge-simulator.vue